// Most of the time you visit this file would be conditionally rendering tab and subTabs
// Please refer `conditionallyMergedChildRoutes` and `currentRoutes` where we can conditionally render tab and subTab

import { buildTabAndSubtabLink } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import { useAppSelector, useLocation } from 'src/utils/Hooks';
import {
  onChargebacksMode,
  onContentGenerationMode,
  onDisplayAdvertisingMode,
  onShortagesMode
} from 'src/store/modules/user/selectors';
import {
  getQueryParamValue,
  getContentV3DisplayConfig,
  isApple,
  getAppSubscriptionTier,
  shouldShowBeaconProTopics,
  getCurrentContentVersion,
  shouldShowAdvertisingIneligible,
  checkInstacartRetailer,
  showContentVariation,
  showContentVariationClient,
  UpdateUrlQueryParams
} from 'src/utils/app';
import { TabRoute, TabRouteWithoutIndex } from 'src/components/CommonV2Components/GenericSideBar/types';
import { isTopicOrTopicGroupType } from 'src/components/BeaconV2Components/ExperimentalLayout/Traffic/utils';
import { findCurrentTabAndSubtab } from 'src/components/CommonV2Components/GenericSideBar/GenericSideBarUtils';
import { buildLinkParams } from 'src/components/EntityPage/LeftNav/LeftNavConfig';

import React from 'react';
import { SidebarLabel } from 'src/components/BeaconV2Components/GenericSidebarNav/GenericSidebar';
import { checkIsAmazonRetailer } from 'src/utils/browser';

export enum BEACON_TABS {
  ADVERTISING = 'advertising',
  SUMMARY = 'sales',
  FORECASTS = 'forecasts',
  SALES = 'sales',
  TRAFFIC = 'traffic',
  PRICES = 'prices',
  CONTENT = 'content',
  CONTENT_V2 = 'contentV2',
  CONTENT_V3 = 'contentV3',
  BUY_BOX = 'buybox',
  REVIEWS = 'reviews',
  INVENTORY = 'inventory',
  OPERATIONS = 'operations',
  CONVERSION = 'conversion',
  COMPLIANCE = 'compliance', // Apple only

  // Content Generation
  GENERATION = 'generation',
  PROFILES = 'profiles',
  LIBRARY = 'library',
  SYNDICATION = 'syndication',
  PROJECTS = 'projects'
}

export const BEACON_CONTENT_GEN_TABS = [
  BEACON_TABS.GENERATION,
  BEACON_TABS.PROFILES,
  BEACON_TABS.LIBRARY,
  BEACON_TABS.SYNDICATION,
  BEACON_TABS.PROJECTS
];

export enum BEACON_SUBTABS {
  KEY_METRICS = 'keymetrics',
  GROWTH_MATRIX = 'growthmatrix',
  WATERFALL = 'waterfall',
  RULES = 'rules',
  RULES_SUMMARY = 'rulesSummary',
  RULES_DETAILS = 'rulesDetails',
  FORECAST_SUMMARY = 'forecastSummary',
  FORECAST_PLANS = 'forecastsPlans',
  FORECAST_ADJUSTMENTS = 'forecastsAdjustments',
  FORECAST_RECOMMENDATIONS = 'recommendations',
  RETAIL_SALES = 'retailSales',
  UNITS_SOLD = 'unitsSold',
  MARGINS = 'retailerGrossMargin',
  RETURNS = 'returnRate',
  TOTAL_CLICKS = 'totalClicks',
  ORGANIC_CLICKS = 'organicClicks',
  OTHER_CLICKS = 'otherClicks',
  AD_CLICKS = 'adClicks',
  DISPLAY_CLICKS = 'dpvViews',
  RETAIL_PRICE = 'retailPrice',
  CONTENT_SCORE = 'contentScore',
  CONTENT_ACCURACY = 'contentAccuracy',
  CONTENT_SUMMARY = 'contentSummary',
  CONTENT_UTILIZATION = 'contentUtilization',
  CONTENT_ACCURACY_V2 = 'contentAccuracyV2',
  CONTENT_KEYWORDS = 'contentKeywords',
  CONTENT_VOICE_AND_TONE = 'contentVoiceAndTone',
  CONTENT_VARIATIONS = 'contentVariations',
  CONTENT_SETTINGS = 'contentSettings',
  CONTENT_UTILIZATION_V3 = 'contentUtilizationV3',
  CONTENT_ACCURACY_V3 = 'contentAccuracyV3',
  CONTENT_KEYWORDS_V3 = 'contentKeywordsV3',
  WIN_PERCENTAGE = 'winPercentage',
  SELLERS = 'sellers',
  // labeled as Summary in BeaconPro
  REVIEW_TRENDS = 'reviewTrends',
  REVIEW_RATINGS = 'reviewRatings',
  REVIEW_REVIEW = 'reviews',
  REVIEW_SENTIMENT = 'reviewSentiment',
  INVENTORY = 'inventory',
  SHORTAGES = 'shortages',
  DISPUTES = 'disputes',
  PURCHASE_RATE = 'purchaseRate',
  MAP_VIOLATIONS = 'mapPrice',
  CHARGEBACKS = 'chargebacks',
  PROMOTIONS = 'promotions',
  SETTINGS = 'settings',

  // Advertising only subtabs
  AD_METRICS = 'adMetrics',
  INELIGIBLE = 'ineligible',
  OPTIMIZATIONS = 'optimizations',
  BUDGET_ENTITY = 'budgetEntity',
  BUDGET_ALLOCATION = 'budgetAllocation',
  BUDGET_CONFIRMATION = 'budgetConfirmation',
  PORTFOLIO_SETTINGS = 'portfolioSettings',
  PORTFOLIO_CAMPAIGNS = 'portfolioCampaigns',
  PORTFOLIO_CONFIRMATION = 'portfolioConfirmation',

  // Apple only subtabs
  KEYWORD_COMPLIANCE = 'keywordCompliance', // TODO: Figure out significance -> &ctab=compliance-keywordaditem&csubtab=unauthorized&ctype=metric
  SELLER_COMPLIANCE = 'sellerCompliance', // TODO: Figure out significance -> &ctab=compliance-selleritem&csubtab=unauthorized&ctype=metric

  // Generation subtabs
  GENERATE = 'generate',
  GENERATION_REVIEW = 'generationReview',
  // Guidelines subtabs
  PROFILES_PROFILE = 'guidelineProfile',
  PROFILES_ACTIVE = 'guidelineProfileActive',
  PROFILES_ARCHIVED = 'guidelineProfileArchived',
  PROFILES_SETTINGS = 'guidelineProfileSettings',
  // Library subtabs
  LIBRARY = 'library',
  LIBRARY_REVIEW = 'libraryReview',
  // Syndication subtabs
  SYNDICATION = 'syndication',
  // Project subtabs
  PROJECTS = 'projects',
  PROJECTS_SETTINGS = 'projectsSettings',
  PROJECTS_PRODUCTS = 'projectsProducts',
  PROJECTS_REVIEW = 'projectsReview',
  EXISTING_SETTING = 'existingSetting',
  EXISTING_REVIEW = 'existingReview'
}

// ! TODO: Insights pages at some point

/**
 * @param location the location object from react router
 * @returns a collection of all tab routes and the current route that is active for use in AppLinkTabs
 */

/**
 * Helper function to build generation-specific links that append '/generate' to entity paths
 */
const buildGenerationTabAndSubtabLink = ({
  app,
  location,
  tab,
  subtab,
  defaultQueryParams = null
}: {
  app: any;
  location: any;
  tab: string;
  subtab: string;
  defaultQueryParams?: UpdateUrlQueryParams;
}) => {
  const { pathname } = location;
  // Remove '/generate' if it already exists to avoid duplication
  const basePath = pathname.replace('/generate', '');
  const generationPath = `${basePath}/generate`;

  const { linkParams } = buildLinkParams({ tab, subtab }, true);
  const { searchParams, compareParams, filterParams } = app.queryParams;
  const showCompareParams = linkParams.includes('ctab') ? '' : compareParams;

  let subtabLink = `${generationPath}${searchParams}&${linkParams}${showCompareParams}${filterParams}`;

  if (defaultQueryParams) {
    const { updateUrlQueryParams } = require('src/utils/app');
    subtabLink = `${generationPath}${searchParams}&${linkParams}${showCompareParams}${filterParams}${updateUrlQueryParams(
      defaultQueryParams,
      null,
      true
    )}`;
  }

  return subtabLink;
};

export const useBeaconRoutes = () => {
  const location = useLocation();
  const app = useAppSelector((state) => state.app);
  const entityType = useAppSelector((state) => state.entityService?.mainEntity?.type);
  const subscriptionTier = getAppSubscriptionTier();
  const subtab = getQueryParamValue('subtab');

  const notProUser = subscriptionTier !== 'pro';
  const { showContentV3 } = getContentV3DisplayConfig();
  const notPrimeUser = subscriptionTier !== 'prime';
  const currentContentVersion = getCurrentContentVersion();
  const showGrowthMatrix = ['client', 'brand', 'category', 'subcategory', 'product', 'segment'].includes(entityType);
  const hideOptimizationsAndIneligible = ['brand', 'category', 'subcategory', 'product', 'adTargets'].includes(
    entityType
  );
  const isFinalReview = getQueryParamValue('finalReviewState') === 'auto';

  // Determine if we're in "existing project" mode vs "new project" mode
  const isExisting = subtab?.toLowerCase().includes('existing');

  const childRoutes: TabRouteWithoutIndex[] = [
    {
      label: 'Summary',
      tabMappedFrom: BEACON_TABS.SALES,
      subtabMappedFrom: BEACON_SUBTABS.KEY_METRICS,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.SALES, subtab: BEACON_SUBTABS.KEY_METRICS }),
      childSubtabs: [
        { label: 'Key Metrics', value: BEACON_SUBTABS.KEY_METRICS, parentTab: BEACON_TABS.SALES },
        ...(showGrowthMatrix
          ? [
              {
                requireSubscription: notProUser,
                label: 'Growth Matrix',
                value: BEACON_SUBTABS.GROWTH_MATRIX,
                parentTab: BEACON_TABS.SALES
              }
            ]
          : []),
        {
          requireSubscription: notProUser,

          label: 'Waterfall',
          value: BEACON_SUBTABS.WATERFALL,
          parentTab: BEACON_TABS.SALES
        }
      ]
    },
    {
      label: notProUser ? <SidebarLabel requireSubscription={notProUser} label="Forecasts" /> : 'Forecasts',
      requireSubscription: notProUser,
      tabMappedFrom: BEACON_TABS.FORECASTS,
      subtabMappedFrom: BEACON_SUBTABS.FORECAST_SUMMARY,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.FORECASTS,
        subtab: BEACON_SUBTABS.FORECAST_SUMMARY,
        defaultQueryParams: { forecastPeriod: 'fullYear', forecastType: 'adjusted' }
      }),
      childSubtabs: [
        { label: 'Summary', value: BEACON_SUBTABS.FORECAST_SUMMARY, parentTab: BEACON_TABS.FORECASTS },
        {
          label: 'Plans',
          value: BEACON_SUBTABS.FORECAST_PLANS,
          parentTab: BEACON_TABS.FORECASTS,
          defaultQueryParams: { adjustmentStatus: ['Published', 'Draft'] }
        },
        {
          label: 'Adjustments',
          value: BEACON_SUBTABS.FORECAST_ADJUSTMENTS,
          parentTab: BEACON_TABS.FORECASTS,
          defaultQueryParams: { adjustmentStatus: ['Published', 'Draft'] }
        },
        {
          label: 'Recommendations',
          value: BEACON_SUBTABS.FORECAST_RECOMMENDATIONS,
          parentTab: BEACON_TABS.FORECASTS
        }
      ]
    },
    {
      label: 'Traffic',
      tabMappedFrom: BEACON_TABS.TRAFFIC,
      subtabMappedFrom: BEACON_SUBTABS.TOTAL_CLICKS,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.TRAFFIC,
        subtab: notProUser && notPrimeUser ? BEACON_SUBTABS.AD_CLICKS : BEACON_SUBTABS.TOTAL_CLICKS
      }),
      childSubtabs: [
        {
          label: 'Total',
          value: BEACON_SUBTABS.TOTAL_CLICKS,
          parentTab: BEACON_TABS.TRAFFIC,
          requireSubscription: notProUser && notPrimeUser
        },
        {
          label: 'Organic',
          value: BEACON_SUBTABS.ORGANIC_CLICKS,
          parentTab: BEACON_TABS.TRAFFIC,
          requireSubscription: notProUser && notPrimeUser
        },
        {
          label: 'Other',
          value: BEACON_SUBTABS.OTHER_CLICKS,
          parentTab: BEACON_TABS.TRAFFIC,
          requireSubscription: notProUser && notPrimeUser
        },
        { label: 'Paid Search', value: BEACON_SUBTABS.AD_CLICKS, parentTab: BEACON_TABS.TRAFFIC },
        ...(onDisplayAdvertisingMode()
          ? [{ label: 'Display', value: BEACON_SUBTABS.DISPLAY_CLICKS, parentTab: BEACON_TABS.TRAFFIC }]
          : [])
      ]
    },
    {
      label:
        notProUser && notPrimeUser ? (
          <SidebarLabel requireSubscription={notProUser && notPrimeUser} label="Conversion" />
        ) : (
          'Conversion'
        ),
      requireSubscription: notProUser && notPrimeUser,
      tabMappedFrom: BEACON_TABS.CONVERSION,
      subtabMappedFrom: BEACON_SUBTABS.PURCHASE_RATE,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.CONVERSION,
        subtab: BEACON_SUBTABS.PURCHASE_RATE
      }),
      childSubtabs: [{ label: 'Summary', value: BEACON_SUBTABS.PURCHASE_RATE, parentTab: BEACON_TABS.CONVERSION }]
    },

    {
      label: 'Sales',
      tabMappedFrom: BEACON_TABS.SALES,
      subtabMappedFrom: BEACON_SUBTABS.RETAIL_SALES,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.SALES, subtab: BEACON_SUBTABS.RETAIL_SALES }),
      childSubtabs: [
        { label: 'Retail Sales', value: BEACON_SUBTABS.RETAIL_SALES, parentTab: BEACON_TABS.SALES },
        { label: 'Units Sold', value: BEACON_SUBTABS.UNITS_SOLD, parentTab: BEACON_TABS.SALES },
        { label: 'Margins', value: BEACON_SUBTABS.MARGINS, parentTab: BEACON_TABS.SALES },
        { label: 'Returns', value: BEACON_SUBTABS.RETURNS, parentTab: BEACON_TABS.SALES }
      ]
    },

    {
      label: 'Prices',
      tabMappedFrom: BEACON_TABS.SALES,
      subtabMappedFrom: BEACON_SUBTABS.RETAIL_PRICE,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.SALES, subtab: BEACON_SUBTABS.RETAIL_PRICE }),
      childSubtabs: [
        { label: 'Summary', value: BEACON_SUBTABS.RETAIL_PRICE, parentTab: BEACON_TABS.SALES },
        { label: 'Promotions', value: BEACON_SUBTABS.PROMOTIONS, parentTab: BEACON_TABS.SALES },
        { label: 'MAP Violations', value: BEACON_SUBTABS.MAP_VIOLATIONS, parentTab: BEACON_TABS.SALES }
      ]
    },
    {
      label: 'Buy Box',
      tabMappedFrom: BEACON_TABS.BUY_BOX,
      subtabMappedFrom: BEACON_SUBTABS.WIN_PERCENTAGE,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.BUY_BOX, subtab: BEACON_SUBTABS.WIN_PERCENTAGE }),
      childSubtabs: [
        { label: 'Summary', value: BEACON_SUBTABS.WIN_PERCENTAGE, parentTab: BEACON_TABS.BUY_BOX },
        { label: 'Sellers', value: BEACON_SUBTABS.SELLERS, parentTab: BEACON_TABS.BUY_BOX }
      ]
    },
    // Content V1 tab
    {
      label: 'Content',
      tabMappedFrom: BEACON_TABS.CONTENT,
      subtabMappedFrom: BEACON_SUBTABS.CONTENT_SCORE,
      hidden: currentContentVersion !== 'content',
      disabled: currentContentVersion !== 'content',
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.CONTENT,
        subtab: BEACON_SUBTABS.CONTENT_SCORE
      }),
      childSubtabs: [
        {
          label: 'Summary',
          value: BEACON_SUBTABS.CONTENT_SUMMARY,
          parentTab: BEACON_TABS.CONTENT,
          requireSubscription: true,
          hidden: showContentV3
        },
        { label: 'Utilization', value: BEACON_SUBTABS.CONTENT_SCORE, parentTab: BEACON_TABS.CONTENT },
        {
          label: 'Accuracy',
          value: BEACON_SUBTABS.CONTENT_ACCURACY,
          parentTab: BEACON_TABS.CONTENT,
          requireSubscription: true
        },
        {
          label: 'Keywords',
          value: BEACON_SUBTABS.CONTENT_KEYWORDS,
          parentTab: BEACON_TABS.CONTENT,
          requireSubscription: true
        },
        {
          label: 'Voice & Tone',
          value: BEACON_SUBTABS.CONTENT_VOICE_AND_TONE,
          parentTab: BEACON_TABS.CONTENT,
          requireSubscription: true,
          hidden: showContentV3
        },
        {
          label: 'Variations',
          value: BEACON_SUBTABS.CONTENT_VARIATIONS,
          parentTab: BEACON_TABS.CONTENT,
          hidden: !(showContentVariation() || showContentVariationClient())
        }
      ]
    },
    // Content V2 tab
    {
      label: 'Content',
      tabMappedFrom: BEACON_TABS.CONTENT_V2,
      subtabMappedFrom: BEACON_SUBTABS.CONTENT_SUMMARY,
      hidden: currentContentVersion !== 'contentV2',
      disabled: currentContentVersion !== 'contentV2',
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.CONTENT_V2,
        subtab: BEACON_SUBTABS.CONTENT_SUMMARY
      }),
      childSubtabs: [
        {
          label: 'Summary',
          value: BEACON_SUBTABS.CONTENT_SUMMARY,
          parentTab: BEACON_TABS.CONTENT_V2
        },
        {
          label: 'Utilization',
          value: BEACON_SUBTABS.CONTENT_UTILIZATION,
          parentTab: BEACON_TABS.CONTENT_V2
        },
        {
          label: 'Accuracy',
          value: BEACON_SUBTABS.CONTENT_ACCURACY_V2,
          parentTab: BEACON_TABS.CONTENT_V2
        },
        {
          label: 'Keywords',
          value: BEACON_SUBTABS.CONTENT_KEYWORDS,
          parentTab: BEACON_TABS.CONTENT_V2
        },
        {
          label: 'Voice & Tone',
          value: BEACON_SUBTABS.CONTENT_VOICE_AND_TONE,
          parentTab: BEACON_TABS.CONTENT_V2
        },
        {
          label: 'Variations',
          value: BEACON_SUBTABS.CONTENT_VARIATIONS,
          parentTab: BEACON_TABS.CONTENT_V2,
          hidden: !(showContentVariation() || showContentVariationClient())
        }
      ]
    },
    // Content V3 tab
    {
      label: 'Content',
      tabMappedFrom: BEACON_TABS.CONTENT_V3,
      subtabMappedFrom: BEACON_SUBTABS.CONTENT_UTILIZATION,
      hidden: currentContentVersion !== 'contentV3',
      disabled: currentContentVersion !== 'contentV3',
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.CONTENT_V3,
        subtab: BEACON_SUBTABS.CONTENT_UTILIZATION
      }),
      childSubtabs: [
        {
          label: 'Utilization',
          value: BEACON_SUBTABS.CONTENT_UTILIZATION,
          parentTab: BEACON_TABS.CONTENT_V3
        },
        {
          label: 'Accuracy',
          value: BEACON_SUBTABS.CONTENT_ACCURACY,
          parentTab: BEACON_TABS.CONTENT_V3,
          requireSubscription: notProUser
        },
        {
          label: 'Keywords',
          value: BEACON_SUBTABS.CONTENT_KEYWORDS,
          parentTab: BEACON_TABS.CONTENT_V3,
          requireSubscription: notProUser
        },
        {
          label: 'Variations',
          value: BEACON_SUBTABS.CONTENT_VARIATIONS,
          parentTab: BEACON_TABS.CONTENT_V3,
          hidden: !(showContentVariation() || showContentVariationClient())
        }
      ]
    },
    {
      label: 'Reviews',
      tabMappedFrom: BEACON_TABS.REVIEWS,
      subtabMappedFrom: notProUser ? BEACON_SUBTABS.REVIEW_REVIEW : BEACON_SUBTABS.REVIEW_TRENDS,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.REVIEWS,
        subtab: notProUser ? BEACON_SUBTABS.REVIEW_REVIEW : BEACON_SUBTABS.REVIEW_TRENDS
      }),
      childSubtabs: [
        {
          label: 'Summary',
          value: BEACON_SUBTABS.REVIEW_TRENDS,
          parentTab: BEACON_TABS.REVIEWS,
          requireSubscription: notProUser
        },
        {
          label: 'Ratings',
          value: BEACON_SUBTABS.REVIEW_RATINGS,
          parentTab: BEACON_TABS.REVIEWS,
          requireSubscription: notProUser
        },
        { label: 'Reviews', value: BEACON_SUBTABS.REVIEW_REVIEW, parentTab: BEACON_TABS.REVIEWS },
        {
          label: 'Sentiment',
          value: BEACON_SUBTABS.REVIEW_SENTIMENT,
          parentTab: BEACON_TABS.REVIEWS,
          requireSubscription: notProUser
        }
      ]
    },
    {
      label: 'Inventory',
      tabMappedFrom: BEACON_TABS.SALES,
      subtabMappedFrom: BEACON_SUBTABS.INVENTORY,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.SALES, subtab: BEACON_SUBTABS.INVENTORY }),
      childSubtabs: [{ label: 'Summary', value: BEACON_SUBTABS.INVENTORY, parentTab: BEACON_TABS.SALES }]
    },
    ...(onChargebacksMode() || onShortagesMode()
      ? [
          {
            label: 'Operations',
            tabMappedFrom: BEACON_TABS.OPERATIONS,
            subtabMappedFrom: BEACON_SUBTABS.CHARGEBACKS,
            hidden: true,
            disabled: true,
            route: buildTabAndSubtabLink({
              app,
              location,
              tab: BEACON_TABS.OPERATIONS,
              subtab: onChargebacksMode() ? BEACON_SUBTABS.CHARGEBACKS : BEACON_SUBTABS.SHORTAGES
            }),
            childSubtabs: [
              ...(onChargebacksMode()
                ? [{ label: 'Chargebacks', value: BEACON_SUBTABS.CHARGEBACKS, parentTab: BEACON_TABS.OPERATIONS }]
                : []),
              ...(onShortagesMode()
                ? [{ label: 'Shortages', value: BEACON_SUBTABS.SHORTAGES, parentTab: BEACON_TABS.OPERATIONS }]
                : [])
            ]
          }
        ]
      : []),
    {
      label: 'Compliance',
      tabMappedFrom: BEACON_TABS.COMPLIANCE,
      subtabMappedFrom: BEACON_SUBTABS.KEY_METRICS,
      hidden: !isApple() || !checkIsAmazonRetailer(),
      disabled: !isApple() || !checkIsAmazonRetailer(),
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.COMPLIANCE,
        subtab: BEACON_SUBTABS.KEY_METRICS
      }),
      childSubtabs: [
        { label: 'Summary', value: BEACON_SUBTABS.KEY_METRICS, parentTab: BEACON_TABS.COMPLIANCE },
        { label: 'Advertising', value: BEACON_SUBTABS.KEYWORD_COMPLIANCE, parentTab: BEACON_TABS.COMPLIANCE },
        { label: 'Sellers', value: BEACON_SUBTABS.SELLER_COMPLIANCE, parentTab: BEACON_TABS.COMPLIANCE }
      ]
    }
  ];

  const generationRoutes: TabRouteWithoutIndex[] = [
    {
      label: 'Profiles',
      tabMappedFrom: BEACON_TABS.PROFILES,
      subtabMappedFrom: BEACON_SUBTABS.PROFILES_ACTIVE,
      hidden: !onContentGenerationMode(),
      route: buildGenerationTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.PROFILES,
        subtab: BEACON_SUBTABS.PROFILES_ACTIVE
      }),
      childSubtabs: [
        {
          label: 'Active',
          value: BEACON_SUBTABS.PROFILES_ACTIVE,
          parentTab: BEACON_TABS.PROFILES,
          hidden: subtab === BEACON_SUBTABS.PROFILES_SETTINGS
        },
        {
          label: 'Archived',
          value: BEACON_SUBTABS.PROFILES_ARCHIVED,
          parentTab: BEACON_TABS.PROFILES,
          hidden: subtab === BEACON_SUBTABS.PROFILES_SETTINGS
        },
        {
          label: 'Settings',
          value: BEACON_SUBTABS.PROFILES_SETTINGS,
          parentTab: BEACON_TABS.PROFILES,
          hidden: subtab === BEACON_SUBTABS.PROFILES_ACTIVE || subtab === BEACON_SUBTABS.PROFILES_ARCHIVED
        }
      ]
    },
    {
      label: 'Projects',
      tabMappedFrom: BEACON_TABS.PROJECTS,
      subtabMappedFrom: BEACON_SUBTABS.PROJECTS,
      hidden: !onContentGenerationMode(),
      route: buildGenerationTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.PROJECTS,
        subtab: BEACON_SUBTABS.PROJECTS
      }),
      childSubtabs: [
        {
          label: 'Summary',
          value: BEACON_SUBTABS.PROJECTS,
          parentTab: BEACON_TABS.PROJECTS,
          hidden: subtab !== BEACON_SUBTABS.PROJECTS
        },
        {
          label: 'Settings',
          value: BEACON_SUBTABS.PROJECTS_SETTINGS,
          parentTab: BEACON_TABS.PROJECTS,
          hidden: subtab === BEACON_SUBTABS.PROJECTS || isExisting,
          disabled: true
        },
        {
          label: 'Products',
          value: BEACON_SUBTABS.PROJECTS_PRODUCTS,
          parentTab: BEACON_TABS.PROJECTS,
          hidden: subtab === BEACON_SUBTABS.PROJECTS || isExisting || isFinalReview,
          disabled: true
        },
        {
          label: 'Review',
          value: BEACON_SUBTABS.PROJECTS_REVIEW,
          parentTab: BEACON_TABS.PROJECTS,
          hidden: subtab === BEACON_SUBTABS.PROJECTS || isExisting,
          disabled: true
        },
        {
          label: 'Settings',
          value: BEACON_SUBTABS.EXISTING_SETTING,
          parentTab: BEACON_TABS.PROJECTS,
          hidden: subtab === BEACON_SUBTABS.PROJECTS || !isExisting
        },
        {
          label: 'Review',
          value: BEACON_SUBTABS.EXISTING_REVIEW,
          parentTab: BEACON_TABS.PROJECTS,
          hidden: subtab === BEACON_SUBTABS.PROJECTS || !isExisting
        }
      ]
    },
    {
      label: 'Library',
      tabMappedFrom: BEACON_TABS.LIBRARY,
      subtabMappedFrom: BEACON_SUBTABS.LIBRARY,
      hidden: !onContentGenerationMode(),
      route: buildGenerationTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.LIBRARY,
        subtab: BEACON_SUBTABS.LIBRARY
      }),
      childSubtabs: [
        { label: 'Summary', value: BEACON_SUBTABS.LIBRARY, parentTab: BEACON_TABS.LIBRARY },
        {
          label: 'Review',
          value: BEACON_SUBTABS.LIBRARY_REVIEW,
          parentTab: BEACON_TABS.LIBRARY,
          disabled: true,
          hidden: subtab === BEACON_SUBTABS.LIBRARY
        }
      ]
    }
  ];

  const financeRoutes: TabRouteWithoutIndex[] = [
    {
      label: 'Disputes',
      tabMappedFrom: BEACON_TABS.OPERATIONS,
      subtabMappedFrom: BEACON_SUBTABS.DISPUTES,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.OPERATIONS, subtab: BEACON_SUBTABS.DISPUTES }),
      childSubtabs: [{ label: 'Summary', value: BEACON_SUBTABS.DISPUTES, parentTab: BEACON_TABS.OPERATIONS }]
    }
  ];

  const getAdvertisingSubtabs = () => {
    const showIneligible = shouldShowAdvertisingIneligible();
    const subtabs = [{ label: 'Ad Metrics', value: BEACON_SUBTABS.AD_METRICS, parentTab: BEACON_TABS.ADVERTISING }];

    if (!hideOptimizationsAndIneligible) {
      if (showIneligible) {
        subtabs.push({ label: 'Ineligible', value: BEACON_SUBTABS.INELIGIBLE, parentTab: BEACON_TABS.ADVERTISING });
      }

      if (!checkInstacartRetailer()) {
        subtabs.push({
          label: 'Optimizations',
          value: BEACON_SUBTABS.OPTIMIZATIONS,
          parentTab: BEACON_TABS.ADVERTISING
        });
      }

      return subtabs;
    }
  };

  const advertisingRoutes: TabRouteWithoutIndex[] = [
    {
      label: 'Summary',
      tabMappedFrom: BEACON_TABS.ADVERTISING,
      subtabMappedFrom: BEACON_SUBTABS.AD_METRICS,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.ADVERTISING, subtab: BEACON_SUBTABS.AD_METRICS }),
      childSubtabs: getAdvertisingSubtabs()
    },
    {
      label: 'Portfolio',
      tabMappedFrom: BEACON_TABS.ADVERTISING,
      subtabMappedFrom: BEACON_SUBTABS.PORTFOLIO_SETTINGS,
      hidden: true,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.ADVERTISING,
        subtab: BEACON_SUBTABS.PORTFOLIO_SETTINGS
      }),
      childSubtabs: [
        { label: 'Settings', value: BEACON_SUBTABS.PORTFOLIO_SETTINGS, parentTab: BEACON_TABS.ADVERTISING },
        { label: 'Campaigns', value: BEACON_SUBTABS.PORTFOLIO_CAMPAIGNS, parentTab: BEACON_TABS.ADVERTISING },
        { label: 'Confirmation', value: BEACON_SUBTABS.PORTFOLIO_CONFIRMATION, parentTab: BEACON_TABS.ADVERTISING }
      ]
    },
    {
      label: 'Budget Management',
      tabMappedFrom: BEACON_TABS.ADVERTISING,
      subtabMappedFrom: BEACON_SUBTABS.BUDGET_ENTITY,
      hidden: true,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.ADVERTISING,
        subtab: BEACON_SUBTABS.BUDGET_ENTITY
      }),
      childSubtabs: [
        { label: 'Entity', value: BEACON_SUBTABS.BUDGET_ENTITY, parentTab: BEACON_TABS.ADVERTISING },
        { label: 'Allocation', value: BEACON_SUBTABS.BUDGET_ALLOCATION, parentTab: BEACON_TABS.ADVERTISING },
        { label: 'Confirmation', value: BEACON_SUBTABS.BUDGET_CONFIRMATION, parentTab: BEACON_TABS.ADVERTISING }
      ]
    },
    {
      label: 'Rules',
      tabMappedFrom: BEACON_TABS.ADVERTISING,
      subtabMappedFrom: BEACON_SUBTABS.RULES,
      hidden: true,
      route: buildTabAndSubtabLink({
        app,
        location,
        tab: BEACON_TABS.ADVERTISING,
        subtab: BEACON_SUBTABS.RULES
      }),
      childSubtabs: [
        { label: 'Summary', value: BEACON_SUBTABS.RULES_SUMMARY, parentTab: BEACON_TABS.ADVERTISING },
        {
          label: 'Details',
          value: BEACON_SUBTABS.RULES_DETAILS,
          parentTab: BEACON_TABS.ADVERTISING,
          disabled: true
        }
      ]
    }
  ];

  const topicsRoutes: TabRouteWithoutIndex[] = [
    {
      label: 'Traffic',
      tabMappedFrom: BEACON_TABS.TRAFFIC,
      subtabMappedFrom: BEACON_SUBTABS.TOTAL_CLICKS,
      route: buildTabAndSubtabLink({ app, location, tab: BEACON_TABS.TRAFFIC, subtab: BEACON_SUBTABS.TOTAL_CLICKS }),
      childSubtabs: [
        { label: 'Total', value: BEACON_SUBTABS.TOTAL_CLICKS, parentTab: BEACON_TABS.TRAFFIC },
        { label: 'Organic', value: BEACON_SUBTABS.ORGANIC_CLICKS, parentTab: BEACON_TABS.TRAFFIC },
        { label: 'Paid Search', value: BEACON_SUBTABS.AD_CLICKS, parentTab: BEACON_TABS.TRAFFIC },
        ...(shouldShowBeaconProTopics() && isTopicOrTopicGroupType(entityType)
          ? [{ label: 'Settings', value: BEACON_SUBTABS.SETTINGS, parentTab: BEACON_TABS.TRAFFIC }]
          : [])
      ]
    }
  ];

  // Create a new array with dynamically assigned indices. This allows MUI to avoid gaps in indices in order to reconcile which tab is selected.useBeaconRoutes
  const defaultChildRoutesWithIndexes: TabRoute[] = childRoutes.map((route, index) => ({
    ...route,
    index
  }));

  const financeRoutesWithIndexes: TabRoute[] = financeRoutes.map((route, index) => ({
    ...route,
    index
  }));

  const advertisingRoutesWithIndexes: TabRoute[] = advertisingRoutes.map((route, index) => ({
    ...route,
    index
  }));

  const topicsRoutesWithIndexes: TabRoute[] = topicsRoutes.map((route, index) => ({ ...route, index }));

  const generationRoutesWithIndexes: TabRoute[] = generationRoutes.map((route, index) => ({ ...route, index }));

  // made it as a full length function, so we can add many conditions as we want later.
  const conditionallyMergedChildRoutes = (): TabRoute[] => {
    const baseRoutes =
      entityType === 'searchtermlist' || entityType === 'topic' || entityType === 'topicgroup'
        ? [...topicsRoutesWithIndexes, ...financeRoutesWithIndexes]
        : [...defaultChildRoutesWithIndexes, ...financeRoutesWithIndexes, ...advertisingRoutesWithIndexes];

    if (onContentGenerationMode()) {
      baseRoutes.push(...generationRoutesWithIndexes);
    }

    return baseRoutes;
  };

  const { currentRoute, currentSubtab }: { currentRoute: TabRoute; currentSubtab: string } = findCurrentTabAndSubtab({
    location,
    childRoutes: conditionallyMergedChildRoutes()
  });

  const getCurrentRoutes = () => {
    const tab = getQueryParamValue('tab');
    const subtab = getQueryParamValue('subtab');

    const isContentGeneration = location.pathname.includes('/generate');

    if (tab === 'advertising') {
      return advertisingRoutesWithIndexes;
    }
    if (isContentGeneration) {
      return generationRoutesWithIndexes;
    }
    if (tab === 'operations' && ['disputes'].includes(subtab)) {
      return financeRoutesWithIndexes;
    }
    if (entityType === 'searchtermlist' || entityType === 'topic' || entityType === 'topicgroup') {
      return topicsRoutesWithIndexes;
    }
    return defaultChildRoutesWithIndexes;
  };

  return {
    routes: {
      defaultRoutes: defaultChildRoutesWithIndexes,
      financeRoutes: financeRoutesWithIndexes,
      topicsRoutes: topicsRoutesWithIndexes,
      advertisingRoutes: advertisingRoutesWithIndexes,
      generationRoutes: generationRoutesWithIndexes
    },
    currentRoute,
    getCurrentRoutes,
    currentSubtab
  };
};
